import * as Sentry from '@sentry/vue';
import { useAuthStore } from '../stores/auth';

const SENTRY_DSN =
  'https://577f4e317e67c948af925627e989362f@o4508954925531136.ingest.de.sentry.io/4508954939686992';

const isSentryBlocked = () => {
  const isBrave =
    typeof navigator !== 'undefined' &&
    (navigator.brave?.isBrave || navigator.userAgent?.includes('Brave'));
  const hasBlocker =
    typeof window !== 'undefined' &&
    (window.__ublock !== undefined ||
      window.__adblock !== undefined ||
      window.blockAdBlock !== undefined);
  return isBrave || hasBlocker;
};

const shouldRespectDnt = () =>
  typeof navigator !== 'undefined' && navigator.doNotTrack === '1';

export default {
  install(app, { router } = {}) {
    const toast = () => app.config.globalProperties?.$toast;
    const handleUnauthorized = () => {
      try {
        const auth = useAuthStore();
        auth.logout();
      } catch (e) {
        console.error('Failed to logout after unauthorized response:', e);
      }
      toast()?.error?.('Session expired, please sign in again.');
      if (router) {
        router.push({ name: 'auth' }).catch(() => {});
      }
    };

    const captureError = (err, context = '') => {
      if ([401, 403].includes(err?.response?.status)) {
        handleUnauthorized();
        return;
      }
      console.error(context || 'Captured error', err);
      if (typeof Sentry.captureException === 'function') {
        const errorObject = err instanceof Error ? err : new Error(err?.message || context);
        Sentry.captureException(errorObject);
      }
    };

    app.config.errorHandler = (error, instance, info) => {
      captureError(error, `Vue error handler (${info})`);
      // Only emit if the component explicitly declares an error listener
      const declaresError =
        Array.isArray(instance?.$options?.emits) &&
        instance.$options.emits.includes('error');
      const declaresErrorObj =
        instance?.$options?.emits &&
        typeof instance.$options.emits === 'object' &&
        Object.prototype.hasOwnProperty.call(instance.$options.emits, 'error');
      if (instance?.$emit && (declaresError || declaresErrorObj)) {
        instance.$emit('error', error);
      }
    };

    window.addEventListener('error', (event) => {
      if (event?.error) {
        captureError(event.error, 'window.error');
      }
    });

    window.addEventListener('unhandledrejection', (event) => {
      if (event?.reason) {
        captureError(event.reason, 'unhandledrejection');
      }
    });

    if (isSentryBlocked()) {
      return;
    }

    try {
      Sentry.init({
        app,
        dsn: SENTRY_DSN,
        integrations: [
          Sentry.browserTracingIntegration({ router }),
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
            textSelector: '.sentry-mask',
            maskInputs: true,
            beforeAddRecordingEvent(event) {
              if (shouldRespectDnt()) {
                return null;
              }
              return event;
            },
          }),
        ],
        tracesSampleRate: 1.0,
        tracePropagationTargets: [],
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        profilesSampleRate: 1.0,
        beforeSend(event) {
          if (shouldRespectDnt()) {
            return null;
          }
          return event;
        },
      });
    } catch (error) {
      console.error('Failed to initialize Sentry:', error);
    }
  },
};
